/**
 * PanelHeader.tsx
 *
 * The strip at the top of column 3. One line of identity, one line of context,
 * actions on the right — the same shape whichever panel is showing, so the
 * technician's eye lands in the same place every time they change step.
 */

import React from "react";
import { Typography } from "antd";

const { Text } = Typography;

interface PanelHeaderProps {
	/** Small uppercase eyebrow above the title, e.g. "Step 3 · Manual". */
	eyebrow?: React.ReactNode;
	title: React.ReactNode;
	/** Secondary line — status, counts, timings. */
	meta?: React.ReactNode;
	actions?: React.ReactNode;
}

const PanelHeader: React.FC<PanelHeaderProps> = ({ eyebrow, title, meta, actions }) => (
	<div
		style={{
			display: "flex",
			alignItems: "flex-start",
			justifyContent: "space-between",
			gap: 12,
			flexShrink: 0,
			padding: "10px 16px",
			borderBottom: "1px solid var(--mg-border)",
			background: "var(--mg-bg-elevated)",
		}}
	>
		<div style={{ minWidth: 0 }}>
			{eyebrow && (
				<div
					style={{
						fontSize: 10,
						fontWeight: 600,
						textTransform: "uppercase",
						letterSpacing: "0.06em",
						color: "var(--mg-text-secondary)",
						marginBottom: 1,
					}}
				>
					{eyebrow}
				</div>
			)}
			<div
				style={{
					fontSize: 14,
					fontWeight: 600,
					color: "var(--mg-text)",
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
				}}
			>
				{title}
			</div>
			{meta && (
				<div style={{ marginTop: 2 }}>
					{typeof meta === "string" ? (
						<Text type="secondary" style={{ fontSize: 11 }}>
							{meta}
						</Text>
					) : (
						meta
					)}
				</div>
			)}
		</div>

		{actions && (
			<div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>{actions}</div>
		)}
	</div>
);

export default PanelHeader;
